import {
  buildGroupedRows,
  isGroupHeaderRow,
  resolveGroupedRowsLoadMoreIndex,
  type GroupHeaderRowBase,
} from '@terra/ui-design-system';
import {
  InternalNetworkAdEntityKindSchema,
  type AdEntityListGroupByField,
  type AdEntityListGroupDto,
  type AdEntityListOrderByDto,
  type AdEntityListSortField,
  type InternalNetworkAdEntityKind,
  type InternalNetworkAdEntitySummaryDto,
  type InternalNetworkAssetListOrderByDto,
  type InternalNetworkAssetListSortField,
  type InternalNetworkAssetSummaryDto,
} from '@terra/shared-contracts-internal-network';
import type { ListPageTableSort } from '../../../common/list-page-infinite-table.model';
import type { SurfaceAssetFilteredKindCounts } from '../surface-asset-kind-count';

export const INTERNAL_ASSETS_PAGE_SIZE = 50;
export const INTERNAL_ASSETS_MIN_SEARCH_LENGTH = 2;
export const INTERNAL_ASSETS_GROUP_HEADER_ID_PREFIX =
  '__internal_assets_group__:';
export const INTERNAL_ASSETS_EMPTY_GROUP_KEY = '';

export type InternalAssetsListMode = 'ad' | 'canonical';

export type InternalAdAssetGridItem = {
  source: 'ad';
  rowId: string;
  label: string;
  entity: InternalNetworkAdEntitySummaryDto;
};

export type InternalCanonicalAssetGridItem = {
  source: 'canonical';
  rowId: string;
  label: string;
  asset: InternalNetworkAssetSummaryDto;
};

export type InternalAssetGridItem =
  | InternalAdAssetGridItem
  | InternalCanonicalAssetGridItem;

export type InternalAssetsGroupHeaderRow = GroupHeaderRowBase & {
  entityKind: InternalNetworkAdEntityKind | null;
};

export type InternalAssetsTableRow =
  | InternalAssetGridItem
  | InternalAssetsGroupHeaderRow;

export const isInternalAssetsGroupHeaderRow = (
  row: InternalAssetsTableRow | null | undefined,
): row is InternalAssetsGroupHeaderRow => isGroupHeaderRow(row);

export const INTERNAL_AD_ASSETS_SORT_COLUMNS = [
  'displayName',
  'isCrownJewel',
  'isOwned',
  'samAccountName',
  'entityKind',
  'firstObservedAt',
] as const satisfies readonly AdEntityListSortField[];

export const INTERNAL_CANONICAL_ASSETS_SORT_COLUMNS = [
  'displayName',
  'assetKind',
  'lastObservedAt',
] as const satisfies readonly InternalNetworkAssetListSortField[];

type InternalAdAssetsSortColumn =
  (typeof INTERNAL_AD_ASSETS_SORT_COLUMNS)[number];
type InternalCanonicalAssetsSortColumn =
  (typeof INTERNAL_CANONICAL_ASSETS_SORT_COLUMNS)[number];

export type InternalAssetsSortColumn =
  | InternalAdAssetsSortColumn
  | InternalCanonicalAssetsSortColumn;

export type InternalAssetsSort = ListPageTableSort<InternalAssetsSortColumn>;

const isInternalAdSortColumn = (
  colId: string,
): colId is InternalAdAssetsSortColumn =>
  INTERNAL_AD_ASSETS_SORT_COLUMNS.some((column) => column === colId);

const isInternalCanonicalSortColumn = (
  colId: string,
): colId is InternalCanonicalAssetsSortColumn =>
  INTERNAL_CANONICAL_ASSETS_SORT_COLUMNS.some((column) => column === colId);

export const isInternalAssetsSortColumn = (
  colId: string,
): colId is InternalAssetsSortColumn =>
  isInternalAdSortColumn(colId) || isInternalCanonicalSortColumn(colId);

export const isInternalAssetsSortColumnForMode = ({
  mode,
  colId,
}: {
  mode: InternalAssetsListMode;
  colId: string;
}): boolean =>
  mode === 'ad'
    ? isInternalAdSortColumn(colId)
    : isInternalCanonicalSortColumn(colId);

export const toInternalAdOrderBy = (
  sort: InternalAssetsSort,
): AdEntityListOrderByDto[] | undefined =>
  sort && isInternalAdSortColumn(sort.colId)
    ? [{ field: sort.colId, order: sort.sort }]
    : undefined;

export const toInternalCanonicalOrderBy = (
  sort: InternalAssetsSort,
): InternalNetworkAssetListOrderByDto[] | undefined =>
  sort && isInternalCanonicalSortColumn(sort.colId)
    ? [{ field: sort.colId, order: sort.sort }]
    : undefined;

export type InternalAssetsGroupByField = AdEntityListGroupByField | 'assetKind';

const AD_KIND_GROUP_BY_OPTIONS: readonly InternalAssetsGroupByField[] = [
  'isCrownJewel',
  'isOwned',
];
const CANONICAL_GROUP_BY_OPTIONS: readonly InternalAssetsGroupByField[] = [
  'assetKind',
];
const NO_GROUP_BY_OPTIONS: readonly InternalAssetsGroupByField[] = [];

export const resolveInternalAssetsGroupByOptions = ({
  mode,
  isAllKinds,
  sortApplied,
}: {
  mode: InternalAssetsListMode;
  isAllKinds: boolean;
  sortApplied: boolean;
}): readonly InternalAssetsGroupByField[] => {
  if (mode === 'canonical') {
    return CANONICAL_GROUP_BY_OPTIONS;
  }

  return isAllKinds || !sortApplied
    ? NO_GROUP_BY_OPTIONS
    : AD_KIND_GROUP_BY_OPTIONS;
};

export const resolveInternalAssetsGroupBy = ({
  mode,
  isAllKinds,
  sortApplied,
  requested,
}: {
  mode: InternalAssetsListMode;
  isAllKinds: boolean;
  sortApplied: boolean;
  requested: InternalAssetsGroupByField | null;
}): InternalAssetsGroupByField | undefined => {
  if (mode === 'ad' && isAllKinds) {
    return 'entityKind';
  }

  return resolveInternalAssetsGroupByOptions({
    mode,
    isAllKinds,
    sortApplied,
  }).find((field) => field === requested);
};

export const getInternalAssetGroupKey = ({
  item,
  groupBy,
}: {
  item: InternalAssetGridItem;
  groupBy: InternalAssetsGroupByField;
}): string => {
  if (item.source === 'canonical') {
    return item.asset.assetKind;
  }

  switch (groupBy) {
    case 'entityKind':
      return item.entity.entityKind;
    case 'isCrownJewel':
      return String(item.entity.isCrownJewel);
    case 'isOwned':
      return String(item.entity.isOwned);
    case 'assetKind':
      return INTERNAL_ASSETS_EMPTY_GROUP_KEY;
  }
};

export const buildInternalAssetsTableRows = ({
  items,
  groupBy,
  groups,
  collapsedGroupKeys,
  getGroupLabel,
}: {
  items: readonly InternalAssetGridItem[];
  groupBy: InternalAssetsGroupByField | undefined;
  groups: readonly AdEntityListGroupDto[];
  collapsedGroupKeys: ReadonlySet<string>;
  getGroupLabel: (groupKey: string) => string;
}): InternalAssetsTableRow[] => {
  if (!groupBy) {
    return [...items];
  }

  const countByKey = new Map<string, number>(
    groups.map((group) => [
      group.key ?? INTERNAL_ASSETS_EMPTY_GROUP_KEY,
      group.count,
    ]),
  );

  return buildGroupedRows<InternalAssetGridItem, InternalAssetsGroupHeaderRow>({
    items,
    getItemId: (item) => item.rowId,
    getGroupKey: (item) => getInternalAssetGroupKey({ item, groupBy }),
    getGroupLabel,
    collapsedGroupKeys,
    headerIdPrefix: INTERNAL_ASSETS_GROUP_HEADER_ID_PREFIX,
    knownGroupKeys: [...countByKey.keys()],
    decorateHeader: (header) => {
      const entityKind =
        groupBy === 'entityKind'
          ? InternalNetworkAdEntityKindSchema.safeParse(header.groupKey)
          : null;

      return {
        ...header,
        count: countByKey.get(header.groupKey) ?? header.count,
        entityKind: entityKind?.success ? entityKind.data : null,
      };
    },
  });
};

export const resolveInternalAssetsLoadMoreRowIndex = ({
  rows,
  groupBy,
  collapsedGroupKeys,
}: {
  rows: readonly InternalAssetsTableRow[];
  groupBy: InternalAssetsGroupByField | undefined;
  collapsedGroupKeys: ReadonlySet<string>;
}): number | null =>
  groupBy
    ? resolveGroupedRowsLoadMoreIndex({
        rows,
        collapsedGroupKeys,
        hasUnseenGroups: false,
      })
    : rows.length - 1;

export const toInternalAssetsSearchTerm = (search: string): string | null => {
  const normalized = search.trim();

  return normalized.length >= INTERNAL_ASSETS_MIN_SEARCH_LENGTH
    ? normalized
    : null;
};

export const INTERNAL_ASSETS_CANONICAL_KIND_KEY = 'CANONICAL';

export const toInternalAssetsSearchKindCounts = ({
  adGroups,
  canonicalTotal,
}: {
  adGroups: readonly AdEntityListGroupDto[] | undefined;
  canonicalTotal: number;
}): SurfaceAssetFilteredKindCounts | null => {
  if (!adGroups) {
    return null;
  }

  const adCountsByKind = adGroups.flatMap((group) =>
    group.key === null ? [] : [[group.key, group.count] as const],
  );

  return {
    byKind: new Map<string, number>([
      ...adCountsByKind,
      [INTERNAL_ASSETS_CANONICAL_KIND_KEY, canonicalTotal],
    ]),
    total: adCountsByKind.reduce((sum, [, count]) => sum + count, 0),
  };
};
